import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchBaseSnapshot } from "../../services";
import { AppDispatch, RootState } from "../store";
import { getAssets } from "../assets/assetsSlice";
import { ILightSnapshot } from "../../types/leanTypes/lightSnapshot";
import { DetailedPolicy } from "../../types/newTypes/DetailedSafeObjects/DetailedPolicy";

// Define serializable state type
interface SnapshotState {
  // data: Record<string, any> | null; // Store serialized Snapshot data
  data: ILightSnapshot | null; // Store serialized Snapshot data
  selectedGroup: string | null;
  policies: DetailedPolicy[];
  status: "init" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: SnapshotState = {
  data: null,
  selectedGroup: null,
  policies: [],
  status: "init",
  error: null,
};

type GetSnapshotArgs = {
  address: string;
  account: string;
  groupName: string | null;
};

export const getSnapshot = createAsyncThunk<
  // Record<string, any>, // Return serialized data
  ILightSnapshot, // Return serialized data
  GetSnapshotArgs,
  { dispatch: AppDispatch; state: RootState }
>("snapshot/getSnapshot", async ({ address, account, groupName }, { dispatch }) => {
  try {
    const snapshot = await fetchBaseSnapshot({ address });

    dispatch(getAssets({ assets: snapshot.assets }));

    const relevantGroups = snapshot.groups.filter((group: any) =>
      group.nestedUsers.some((user: any) => user.public_key.includes(account))
    );

    const chosenGroup = groupName
      ? relevantGroups.find((group: any) => group.name === groupName)
      : relevantGroups[0];

    dispatch(selectGroup(chosenGroup?.id ?? ""));

    // Return serialized data instead of Snapshot instance
    return snapshot;
  } catch (error) {
    console.error(error);
    throw error; // Let Redux handle the rejection
  }
});

const snapshotSlice = createSlice({
  name: "snapshot",
  initialState,
  reducers: {
    selectGroup: (state, action: PayloadAction<string>) => {
      if (action.payload === "") {
        state.selectedGroup = null;
      } else {
        state.selectedGroup = action.payload;
      }
    },
    clearSelection: (state) => {
      state.selectedGroup = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSnapshot.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getSnapshot.fulfilled, (state, action: PayloadAction<ILightSnapshot>) => {
        state.status = "succeeded";
        state.data = action.payload; // Store serialized data
      })
      .addCase(getSnapshot.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Something went wrong";
      });
  },
});

export const { selectGroup, clearSelection } = snapshotSlice.actions;

// Selector to rehydrate Snapshot instance
export const selectSnapshot = (state: RootState): ILightSnapshot => {
  const data = state.snapshot.data;
  return data;
  // return data ? Snapshot.fromMap(data) : Snapshot.empty();
};

// export const selectSnapshotSafe = (state: RootState): Safe | null => {
//   const data = state.snapshot.data;
//   return data ? Snapshot.fromMap(data).safe : null;
// };

// Selector for convenience (optional)
export const selectSnapshotStatus = (state: RootState): SnapshotState["status"] =>
  state.snapshot.status;
export const selectSelectedGroup = (state: RootState): string | null =>
  state.snapshot.selectedGroup;

export default snapshotSlice.reducer;
